/** @format */

import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';

import Master from '../Layouts/Master';
import SidebarForm from './SidebarForm';
import useApi from '../../Shared/hooks/api/Index';
import { error, success } from '../../Shared/Components/Toast';
import Loading from '../../Shared/Components/Loading';
import { EventContext } from '../../Contexts/EventContext';
import ErrorMessage from '../../Shared/Components/ErrorMessage';
import SwitchButton from '../../Shared/Components/SwitchButton';

const validationSchema = Yup.object().shape({
	title: Yup.string()
		.required('عنوان الزامی است')
		.max(190, 'عنوان نمیتواندبیشتر از 190 کاراکتر باشد'),
});

const EditForm = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const eventContext = useContext(EventContext);
	const [loading, setLoading] = useState(false);
	const [fieldsLoading, setFieldsLoading] = useState(false);
	const [submitFormLoading, setSubmitFormLoading] = useState(false);
	const [loadSwitch, setLoadSwitch] = useState(false);

	useEffect(() => {
		setLoading(true);
		const getInfo = async () => {
			await useApi.get(
				`/form/${id}`,
				{},
				(res) => {
					eventContext.form = res.data.data;
					eventContext.formBuilderFieldsData = res.data.data.fields;
				},
				(err) => {
					setLoading(false);
					error('هنگام دریافت اطلاعات خطایی رخ داده است');
				},
			);
			await useApi.get(
				'/form/fields',
				{},
				(res) => {
					eventContext.fields = res.data.data;
					setLoading(false);
				},
				(err) => {
					setLoading(false);
					error('هنگام دریافت اطلاعات خطایی رخ داده است');
				},
			);
		};
		getInfo();
	}, [id]);

	const addFieldToForm = async (field) => {
		setFieldsLoading(true);
		const fields = [...eventContext.formBuilderFieldsData];
		const fieldIndex = await fields.findIndex((f) => f.id === field.id);

		if (fieldIndex !== -1) {
			setFieldsLoading(false);
			return error(`${field.label} وجود دارد`);
		}

		await fields.push(field);
		eventContext.formBuilderFieldsData = fields;
		setFieldsLoading(false);
	};

	const removeFieldInForm = async (fieldId) => {
		setFieldsLoading(true);
		const fields = [...eventContext.formBuilderFieldsData];
		const filteredFields = await fields.filter((f) => f.id !== fieldId);

		eventContext.formBuilderFieldsData = filteredFields;
		setFieldsLoading(false);
	};

	const onSubmitForm = async (data) => {
		let fields = eventContext.formBuilderFieldsData;
		if (fields.length === 0) {
			return error('لطفا حداقل یک فیلد را اضافه کنید');
		}
		setSubmitFormLoading(true);
		data['fields'] = fields;
		await useApi.put(
			`/form/${id}`,
			data,
			(res) => {
				success('فرم با موفقیت ایجاد شد');

				navigate('/panel/form-builder', { replace: true });
			},
			(err) => {
				setSubmitFormLoading(false);
				error('خطایی در ثبت اطلاعات پیش آمده است.');
			},
		);
	};

	const onChangeFieldLabel = (e, id) => {
		let formBuilderFieldsData = [...eventContext.formBuilderFieldsData];
		let currentFieldIndex = formBuilderFieldsData.findIndex((f) => f.id === id);
		if (currentFieldIndex !== -1) {
			formBuilderFieldsData[currentFieldIndex].label = e.target.value;
		}
		eventContext.formBuilderFieldsData = formBuilderFieldsData;
	};
	const onChangeRequiredField = async (val, id) => {
		setLoadSwitch(true);
		let formBuilderFieldsData = [...eventContext.formBuilderFieldsData];
		let currentFieldIndex = formBuilderFieldsData.findIndex((f) => f.id === id);
		if (currentFieldIndex !== -1) {
			formBuilderFieldsData[currentFieldIndex].validation.required = val;
		}
		eventContext.formBuilderFieldsData = await formBuilderFieldsData;
		setLoadSwitch(false);
	};

	const onChangeForeignersField = async (val, id) => {
		setLoadSwitch(true);
		let formBuilderFieldsData = [...eventContext.formBuilderFieldsData];
		let currentFieldIndex = formBuilderFieldsData.findIndex((f) => f.id === id);
		if (currentFieldIndex !== -1) {
			formBuilderFieldsData[currentFieldIndex].foreigners = val;
		}
		eventContext.formBuilderFieldsData = await formBuilderFieldsData;
		setLoadSwitch(false);
	};

	return loading ? (
		<Loading />
	) : (
		<Master
			showFormSidebar={true}
			sidebarComponent={<SidebarForm addFieldToForm={addFieldToForm} />}>
			<div className='d-flex flex-column border rounded p-2 mt-3'>
				<Formik
					initialValues={{
						title: eventContext.form.title,
					}}
					onSubmit={(values, { setSubmitting }) => {
						onSubmitForm(values, setSubmitting);
					}}
					validationSchema={validationSchema}>
					{({
						values,
						errors,
						handleChange,
						handleSubmit,
						isSubmitting,
						setFieldValue,
					}) => (
						<form onSubmit={handleSubmit}>
							<div className='row'>
								<div className='col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12'>
									<div className='d-flex flex-column p-2 alert alert-warning'>
										<div className='d-flex flex-row'>
											<i className='fad fa-exclamation-triangle f-20 mx-2'></i>
											<span className='text-dark f-15 mx-2 font-weight-bold'>
												رعایت نکات زیر در ایجاد فرم ها ضروری است‌ :
											</span>
										</div>
										<p className='text-dark text-break  f-15 font-weight-bold p-2 m-0'>
											- در صورت ایجاد فرم برای رویداد های انفرادی وجود فیلد
											کدملی و شماره همراه ضروری است.
										</p>
										<p className='text-dark text-break f-15 font-weight-bold p-2 m-0'>
											- برای رویداد های تیمی و رویدادی باید دو نوع فرم را ایجاد
											نمایید فرم اول برای تیم و یا سرپرست و فرم دوم برای ثبت
											اطلاعات زیر مجموعه تیم یا سرپرست.
										</p>
										<p className='text-dark text-break f-15 font-weight-bold p-2 m-0'>
											- در هنگام تعریف فرم های تیمی وجود چهار فیلد استان -
											شهرستان - نام تیم/باشگاه و شماره تماس تیم /باشگاه الزامی
											است.
										</p>
										<p className='text-dark text-break f-15 font-weight-bold p-2 m-0'>
											- در هنگام تعریف فرم های رویدادی وجود چهار فیلد کدملی
											سرپرست و استان و شهرستان و همچنین تاریخ برگزاری رویداد
											الزامی است.
										</p>
										<p className='text-dark text-break f-15 font-weight-bold p-2 m-0'>
											- برای تعریف کد ملی سرپرست می توانید از فیلد کدملی استفاده
											کنید و تنها نام فیلد را به نام سرپرست تغیر دهید.
										</p>
										<p className='text-danger text-break f-15 text-center font-weight-bold p-2 m-0'>
											رعایت نکات گفته شده الزامی است و مسئولیت عدم رعایت نکات
											گفته شده بر عهده سازنده فرم ها خواهد بود.
										</p>
									</div>
								</div>
								<div className='col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12'>
									<div className='form-group d-flex flex-column'>
										<label className='f-14 mx-2'>عنوان</label>
										<input
											value={values.title}
											onChange={handleChange}
											name='title'
											type='text'
											className='form-control my-2'
										/>
										<ErrorMessage error={errors.title} />
									</div>
								</div>

								{fieldsLoading ? null : (
									<>
										{eventContext.formBuilderFieldsData.length > 0 ? (
											<>
												{eventContext.formBuilderFieldsData.map((item) => (
													<div
														key={item.id}
														className='col-12 col-sm-12 col-md-4 col-lg-4 col-xl-4 my-2'>
														<div className='d-flex flex-row justify-content-between bg-gray p-3 shadow-sm rounded border'>
															<div className='d-flex flex-column'>
																<input
																	className='form-control f-13'
																	type='text'
																	placeholder={item?.label}
																	onChange={(e) =>
																		onChangeFieldLabel(e, item.id)
																	}
																/>

																<div className='d-flex flex-row justify-content-between mt-2'>
																	<label>ضروری بودن فیلد</label>
																	{loadSwitch ? null : (
																		<SwitchButton
																			classes='align-self-start '
																			checked={item.validation.required}
																			onChange={(val) => {
																				onChangeRequiredField(
																					!item.validation.required,
																					item.id,
																				);
																			}}
																		/>
																	)}
																</div>
																{item?.foreigners !== undefined && (
																	<div className='d-flex flex-row justify-content-between mt-2'>
																		<label>ثبت نام اتباع</label>
																		{loadSwitch ? null : (
																			<SwitchButton
																				classes='align-self-start '
																				checked={item?.foreigners}
																				onChange={(val) => {
																					onChangeForeignersField(
																						!item?.foreigners,
																						item.id,
																					);
																				}}
																			/>
																		)}
																	</div>
																)}
															</div>

															<i
																onClick={() => removeFieldInForm(item.id)}
																className='fad fa-trash f-20 text-dark c-pointer mx-2 mt-2'></i>
														</div>
													</div>
												))}
											</>
										) : (
											<div className='col-12'>
												<div className='d-flex flex-row justify-content-center  border-top py-3'>
													<i className='fad fa-plus f-18 text-secondary mx-2'></i>
													<span className='f-12 text-secondary font-weight-bold'>
														لطفا یک فیلد را با استفاده از فرم ساز اضافه کنید
													</span>
												</div>
											</div>
										)}
									</>
								)}
								<div className='col-12'>
									<div className='d-flex flex-row justify-content-end'>
										<button className='btn btn-info btn-sm f-14'>
											{submitFormLoading ? (
												<div
													className='spinner-border spinner-border-sm text-white mb-1'
													role='status'></div>
											) : (
												'ویراش فرم'
											)}
										</button>
									</div>
								</div>
							</div>
						</form>
					)}
				</Formik>
			</div>
		</Master>
	);
};

export default EditForm;
